import { createAsyncThunk } from '@reduxjs/toolkit';
import type { OutgoingEvent } from 'jssip/lib/RTCSession';

import type { AppDispatch, RootState } from 'features/app/store/store';
import { setReceivedTwoLineVco } from 'features/call/call-hearing/store';
import { updateCallRecord } from 'features/call/call-base/store';
import {
  callIdSelector,
  selectDeafIpTraceback,
} from 'features/call/call-base/store/selectors';
import { handleError } from 'features/notification/store';
import { sendPartialCallAnalytics } from 'features/sip/helpers';
import {
  getCallPartialFromSipInfoAttributes,
  getMergedCallData,
  handleIpTracebackUpdate,
  handleVcoStateUpdate,
} from 'features/sip/handlers/';
import { CarryOverType } from 'features/vco/enums';
import {
  isPrimaryVcoSenderSelector,
  isTwoLineVcoSelector,
  vco2LineRequested,
} from 'features/vco/store';

export const updateCallRecordFromSipInfo = createAsyncThunk<
  void,
  { event: OutgoingEvent },
  { state: RootState; dispatch: AppDispatch }
>(
  'sip/updateCallRecordFromSipInfo',
  async ({ event }, { dispatch, getState }) => {
    const state = getState();
    const isCallOwner = isPrimaryVcoSenderSelector(state);

    if (!isCallOwner) {
      // Only call owner should be calling modify call record
      return;
    }

    const callId = callIdSelector(state);
    const deafIpTraceback = selectDeafIpTraceback(state);
    const isVco2Line = isTwoLineVcoSelector(state);
    const skipIpExtraction = Boolean(deafIpTraceback);

    const partialCallFromSipInfo = getCallPartialFromSipInfoAttributes(event);
    sendPartialCallAnalytics(dispatch, partialCallFromSipInfo, callId);

    const {
      mergedData: mergedPartialCall,
      ipFromCallInfo: partialCallFromIpAddress,
    } = getMergedCallData(event, skipIpExtraction);

    if (!mergedPartialCall) {
      return;
    }

    handleVcoStateUpdate(mergedPartialCall, dispatch);
    handleIpTracebackUpdate(
      mergedPartialCall,
      partialCallFromIpAddress,
      dispatch
    );

    try {
      const call = await dispatch(updateCallRecord(mergedPartialCall)).unwrap();

      if (
        call &&
        !isVco2Line &&
        call.CarryOverType === CarryOverType.VCO2Line &&
        call.CarryOverPhoneNumber
      ) {
        dispatch(setReceivedTwoLineVco(call.CarryOverPhoneNumber));
        dispatch(vco2LineRequested());
      }
    } catch (error) {
      dispatch(
        handleError({
          error,
          message: 'Failed to update call record',
          disableUserNotification: true,
        })
      );
    }
  }
);
